import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Formik } from "formik";
import { Box, Button, HStack, Icon, Image, Input, Pressable, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes, Scale, VerticalScale } from "../../constants";
import { getOrganisation } from "../../helpers/userHelpers";
import { onboardingDataSelector, setBiometricData } from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";
import { GenderEnum } from "../../types";

interface BiometricDataScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

const BiometricDataScreen: React.FC<BiometricDataScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const onboardingData = useSelector(onboardingDataSelector);

  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  const validationSchema = Yup.object().shape({
    age: Yup.number().min(18).max(120).required(t("onboarding.onboarding_4_biometric_data_screen.valid_age")),
    gender: Yup.mixed<GenderEnum>()
      .oneOf(Object.values(GenderEnum))
      .required(t("onboarding.onboarding_4_biometric_data_screen.select_gender")),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  const onSubmit = (values: FormSchema): void => {
    dispatch(setBiometricData({ age: values.age, gender: values.gender }));
    navigation.navigate(Routes.PhysicalStatsScreen);
  };

  return (
    <Formik
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialValues={validationSchema.cast({
        age: onboardingData.biometricData?.age || 25,
        gender: onboardingData.biometricData?.gender || GenderEnum.FEMALE,
      })}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, errors, touched }) => {
        const isMale = values.gender === GenderEnum.MALE;
        const isFemale = !isMale;

        const handleGenderPress = (gender: GenderEnum): void => {
          setFieldValue("gender", gender);
        };

        return (
          <SafeAreaView style={{ flex: 1 }}>
            <Box flex={1} bg="primary.10">
              <VStack flex={1}>
                {/* Header */}
                <HStack alignItems="center" p={4}>
                  <Pressable onPress={() => navigation.goBack()} testID="back-button">
                    <Icon as={Ionicons} name="arrow-back" size="6" color="function.50" />
                  </Pressable>
                </HStack>

                {/* Content */}
                <VStack flex={1} alignItems="center" p={4} space={4}>
                  {/* Logo or Icon */}
                  {logoUri ? (
                    <Image
                      source={{ uri: logoUri }}
                      style={{ width: Scale(160), height: VerticalScale(60) }}
                      resizeMode="contain"
                      alt="Organization Logo"
                    />
                  ) : (
                    <Icon as={MaterialCommunityIcons} name="robot" size="16" color="primary.50" />
                  )}

                  {/* Subtitle */}
                  <Text fontSize="16" color="function.30" textAlign="center">
                    {t("onboarding.onboarding_4_biometric_data_screen.subtitle")}
                  </Text>

                  {/* Age Section */}
                  <VStack space={2} width="100%" alignItems="center">
                    <Text fontSize="16" fontWeight="bold" color="function.50">
                      {t("onboarding.onboarding_4_biometric_data_screen.age_label")}
                    </Text>
                    <Input
                      placeholder={t("onboarding.onboarding_4_biometric_data_screen.age_placeholder")}
                      keyboardType="numeric"
                      value={String(values.age)}
                      onChangeText={(text) => setFieldValue("age", text)}
                      selectTextOnFocus
                      width="40%"
                      textAlign="center"
                      borderColor="function.20"
                      _focus={{ borderColor: "primary.50" }}
                      testID="age-input"
                    />
                    {errors.age && touched.age ? (
                      <Text color="red.500" textAlign="center" testID="age-error">
                        {errors.age}
                      </Text>
                    ) : null}
                  </VStack>

                  {/* Gender Section */}
                  <VStack space={4} width="100%" alignItems="center">
                    <Text fontSize="16" fontWeight="bold" color="function.50" testID="gender-label">
                      {t("onboarding.onboarding_4_biometric_data_screen.gender_label")}
                    </Text>
                    <HStack space={4} justifyContent="space-around" width="100%">
                      {/* Male Option */}
                      <Pressable
                        onPress={() => handleGenderPress(GenderEnum.MALE)}
                        testID={`gender-option-${GenderEnum.MALE}`}
                        flex={1}
                        maxW="40%"
                      >
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                          borderRadius={theme.borderRadii.card.default}
                          borderWidth={1}
                          borderColor={isMale ? "primary.50" : "function.20"}
                          bg={isMale ? "primary.20" : "primary.10"}
                          shadow={isMale ? "10" : "2"}
                        >
                          <Text fontSize="24" mr={2}>
                            👨
                          </Text>
                          <Text fontSize="16" color={isMale ? "primary.50" : "function.50"}>
                            {t("onboarding.onboarding_4_biometric_data_screen.male")}
                          </Text>
                        </Box>
                      </Pressable>

                      {/* Female Option */}
                      <Pressable
                        onPress={() => handleGenderPress(GenderEnum.FEMALE)}
                        testID={`gender-option-${GenderEnum.FEMALE}`}
                        flex={1}
                        maxW="40%"
                      >
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="center"
                          p={4}
                          borderRadius={theme.borderRadii.card.default}
                          borderWidth={1}
                          borderColor={isFemale ? "primary.50" : "function.20"}
                          bg={isFemale ? "primary.20" : "primary.10"}
                          shadow={isFemale ? "10" : "2"}
                        >
                          <Text fontSize="24" mr={2}>
                            👩
                          </Text>
                          <Text fontSize="16" color={isFemale ? "primary.50" : "function.50"}>
                            {t("onboarding.onboarding_4_biometric_data_screen.female")}
                          </Text>
                        </Box>
                      </Pressable>
                    </HStack>

                    {errors.gender && touched.gender ? (
                      <Text color="red.500" textAlign="center" testID="gender-error">
                        {errors.gender}
                      </Text>
                    ) : null}
                  </VStack>

                  {/* Next Button */}
                  <Button
                    onPress={() => handleSubmit()}
                    bg="primary.50"
                    _pressed={{ bg: "primary.50:alpha.80" }}
                    py={3}
                    px={8}
                    mt={4}
                    borderRadius={theme.borderRadii.button.default}
                    width="80%"
                    testID="next-button"
                  >
                    <Text color="primary.10" fontWeight="bold">
                      {t("onboarding.onboarding_4_biometric_data_screen.next_button")}
                    </Text>
                  </Button>
                </VStack>
              </VStack>
            </Box>
          </SafeAreaView>
        );
      }}
    </Formik>
  );
};

export default BiometricDataScreen;
